import type { Announcements, UniqueIdentifier } from '@dnd-kit/core';

import type { ComponentListItem } from './useCourse';

/**
 * What a screen reader hears while a chapter or divider is moved on the course
 * home: its title and its place in the list. dnd-kit's own words name the
 * item's raw id (#173). The drop is said here once, and nowhere else.
 */
export function createCourseHomeDragAnnouncements(items: ComponentListItem[]): Announcements {
  const count = items.length;
  // The place last announced: dnd-kit reports the item over its own place as
  // soon as it is picked up, which would talk over "Picked up".
  let lastOverId: UniqueIdentifier | null = null;

  const indexOf = (id: UniqueIdentifier) => items.findIndex((item) => item.id === id);
  const nameOf = (id: UniqueIdentifier) => items[indexOf(id)]?.title || 'The item';
  const placeOf = (id: UniqueIdentifier) => `position ${indexOf(id) + 1} of ${count}`;

  return {
    onDragStart({ active }) {
      lastOverId = active.id;
      return `Picked up ${nameOf(active.id)}, at ${placeOf(active.id)}.`;
    },
    onDragOver({ active, over }) {
      const overId = over?.id ?? null;
      if (overId === lastOverId) {
        return undefined;
      }
      lastOverId = overId;
      return overId === null
        ? `${nameOf(active.id)} is not over a place in the list.`
        : `${nameOf(active.id)} is over ${placeOf(overId)}.`;
    },
    onDragEnd({ active, over }) {
      lastOverId = null;
      return over && over.id !== active.id
        ? `Moved ${nameOf(active.id)} to ${placeOf(over.id)}.`
        : `Dropped ${nameOf(active.id)}. It stays at ${placeOf(active.id)}.`;
    },
    onDragCancel({ active }) {
      lastOverId = null;
      return `Move cancelled. ${nameOf(active.id)} stays at ${placeOf(active.id)}.`;
    },
  };
}
